import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FacebookLogo, InstagramLogo, PaperPlaneTilt, LinkBreak } from "@phosphor-icons/react";
import { toast } from "sonner";

const errMsg = (e) => e.response?.data?.detail || "Errore";
const Box = ({ children, testid }) => <div className="rounded-2xl border border-line bg-canvas p-6 space-y-4" data-testid={testid}>{children}</div>;

function PagePicker({ pages, onDone }) {
  const [sel, setSel] = useState("");
  const save = async () => {
    try { const r = await api.post("/meta/select", { page_id: sel }); toast.success(`Collegata: ${r.data.page_name}`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Box testid="meta-page-picker">
      <div className="font-serif text-lg text-ink">Scegli la Pagina della tua struttura</div>
      {pages.map(p => (
        <label key={p.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${sel === p.id ? "border-ink" : "border-line"}`} data-testid={`meta-page-${p.id}`}>
          <input type="radio" name="page" checked={sel === p.id} onChange={() => setSel(p.id)} />
          <FacebookLogo size={20} /> <span className="text-ink">{p.name}</span>
          {p.ig_username ? <span className="text-stone text-sm flex items-center gap-1"><InstagramLogo size={16} />@{p.ig_username}</span>
            : <span className="text-stone text-xs">nessun Instagram collegato</span>}
        </label>
      ))}
      <Button disabled={!sel} onClick={save} data-testid="meta-save-page-btn">Salva Pagina</Button>
    </Box>
  );
}

function Connected({ s, reload }) {
  const [busy, setBusy] = useState(false);
  const [ins, setIns] = useState(null);
  useEffect(() => { api.get("/meta/insights").then(r => setIns(r.data)).catch(() => {}); }, []);
  const test = async () => {
    setBusy(true);
    try { await api.post("/meta/test-post", {}); toast.success("Post di prova pubblicato su Facebook e Instagram"); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const disc = async () => { await api.delete("/meta/disconnect"); toast.success("Scollegato"); reload(); };
  return (
    <Box testid="meta-connected">
      <div className="flex flex-wrap gap-6">
        <div className="flex items-center gap-2 text-ink" data-testid="meta-page-name"><FacebookLogo size={22} weight="fill" />{s.page_name}</div>
        {s.ig_username && <div className="flex items-center gap-2 text-ink" data-testid="meta-ig-username"><InstagramLogo size={22} weight="fill" />@{s.ig_username}</div>}
      </div>
      {ins && <div className="text-sm text-stone" data-testid="meta-insights">Follower Pagina: {ins.page?.followers_count ?? ins.page?.fan_count ?? "—"}
        {ins.instagram && <> · Follower Instagram: {ins.instagram.followers_count} · Post Instagram: {ins.instagram.media_count}</>}</div>}
      {ins?.posts?.length > 0 && <ul className="text-sm space-y-1" data-testid="meta-recent-posts">{ins.posts.map(p => <li key={p.permalink_url} className="truncate text-stone">{p.created_time?.slice(0, 10)} — {p.message || "(senza testo)"}</li>)}</ul>}
      {s.last_test && <div className="text-xs text-stone" data-testid="meta-last-test">Ultimo test: {s.last_test.at?.slice(0, 16).replace("T", " ")} · FB {s.last_test.facebook_id || "—"} · IG {s.last_test.instagram_id || "—"}</div>}
      <label className="flex items-center gap-3 text-sm text-ink" data-testid="meta-auto-publish">
        <input type="checkbox" checked={s.auto_publish} onChange={async (e) => { await api.put(`/meta/auto-publish?on=${e.target.checked}`); reload(); }} data-testid="meta-auto-publish-toggle" />
        Pubblica in automatico i post programmati nel Calendario Editoriale
      </label>
      <div className="flex gap-3 flex-wrap">
        <Button onClick={test} disabled={busy} data-testid="meta-test-post-btn"><PaperPlaneTilt size={16} className="mr-2" />{busy ? "Pubblico..." : "Pubblica post di prova"}</Button>
        <Button variant="outline" onClick={disc} data-testid="meta-disconnect-btn"><LinkBreak size={16} className="mr-2" />Scollega</Button>
      </div>
    </Box>
  );
}

export default function MetaConnect() {
  const [params] = useSearchParams();
  const [s, setS] = useState(null);
  const [pages, setPages] = useState([]);
  const load = () => api.get("/meta/status").then(r => setS(r.data));
  useEffect(() => {
    load();
    const m = params.get("meta");
    if (m === "choose-page") api.get("/meta/pages").then(r => setPages(r.data)).catch(e => toast.error(errMsg(e)));
    if (m === "cancelled") toast.error("Accesso a Facebook annullato");
    if (m === "expired" || m === "error") toast.error("Collegamento non riuscito, riprova");
  }, [params]);
  const connect = async () => { try { const r = await api.get("/meta/start"); window.location.href = r.data.url; } catch (e) { toast.error(errMsg(e)); } };
  if (!s) return null;
  return (
    <div className="space-y-6 max-w-3xl" data-testid="meta-connect">
      <ol className="text-sm text-ink space-y-1 list-decimal pl-5" data-testid="meta-steps">
        <li>Serve una Pagina Facebook della struttura e un account Instagram professionale collegato alla Pagina (Instagram → Impostazioni → Tipo di account e strumenti).</li>
        <li>Premi "Collega Facebook e Instagram", accedi e lascia spuntate la Pagina e l'account Instagram, poi Continua.</li>
        <li>Torni qui: scegli la Pagina, premi "Salva Pagina" e fai un "Post di prova".</li>
        <li>Da quel momento i post del Calendario Editoriale escono da soli all'orario indicato.</li>
      </ol>
      <p className="text-stone">Collega la Pagina Facebook e l'account Instagram della tua struttura: Otelya pubblicherà per te post e contenuti e ti mostrerà i risultati.</p>
      {pages.length > 0 && !s.connected && <PagePicker pages={pages} onDone={() => { setPages([]); load(); }} />}
      {s.connected ? <Connected s={s} reload={load} /> : pages.length === 0 && (
        <Box testid="meta-not-connected">
          <div className="text-ink">Nessuna Pagina collegata.</div>
          <Button onClick={connect} className="bg-[#1877F2] hover:bg-[#166FE5] text-white" data-testid="meta-connect-btn">
            <FacebookLogo size={18} weight="fill" className="mr-2" />Collega Facebook e Instagram
          </Button>
        </Box>
      )}
    </div>
  );
}
